import { useState, useEffect } from "react";
import { Box, Button, TextField } from "@mui/material";
import { Add, Edit, Delete, PictureAsPdf } from "@mui/icons-material";
import {
  fetchHandoverLetters,
  fetchHandoverLetterById,
  createHandoverLetter,
  updateHandoverLetter,
  deleteHandoverLetter,
  openHandoverLetterPdf,
} from "../../api/HandoverLetterService";
import { fetchCustomerPage } from "../../api/CustomerService";
import customerLabel from "../../utils/customerLabel";
import AsyncAutocomplete from "../../components/common/AsyncAutocomplete";
import TableComponent from "../../components/Table";
import ConfirmDialog from "../../components/ConfirmDialog";
import CreateHandoverLetterDialog from "../../components/handover/CreateHandoverLetterDialog";
import { toast } from "react-toastify";
import theme from "../../theme/theme";

const HandoverLettersManagementPage = () => {
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [links, setLinks] = useState([]);
  const [meta, setMeta] = useState({ from: 0, to: 0, total: 0 });
  const [filters, setFilters] = useState({
    customer_id: "",
    search: "",
    from_date: "",
    to_date: "",
  });

  // Create / Edit (one dialog, edit mode when editLetter is set)
  const [openDialog, setOpenDialog] = useState(false);
  const [editLetter, setEditLetter] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Delete
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // PDF
  const [pdfLoading, setPdfLoading] = useState(null);

  const loadLetters = async () => {
    setLoading(true);
    try {
      const params = { ...filters, page: currentPage, per_page: perPage };
      const res = await fetchHandoverLetters(params);
      const payload = res.data || res;
      setLetters(payload?.data || []);
      setLinks(payload?.links || []);
      setMeta({ from: payload?.from || 0, to: payload?.to || 0, total: payload?.total || 0 });
    } catch (err) {
      toast.error(err.message || "Failed to fetch handover letters");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, perPage]);

  useEffect(() => {
    loadLetters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentPage, perPage]);

  // CREATE / UPDATE
  const handleSave = async (data) => {
    setSaving(true);
    setFormErrors({});
    try {
      if (editLetter) {
        await updateHandoverLetter(editLetter.id, data);
        toast.success("Handover letter updated successfully");
      } else {
        await createHandoverLetter(data);
        toast.success("Handover letter created successfully");
      }
      setOpenDialog(false);
      setEditLetter(null);
      loadLetters();
    } catch (err) {
      if (err?.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      } else {
        toast.error(err.message || "Failed to save handover letter");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEditClick = async (row) => {
    try {
      const res = await fetchHandoverLetterById(row.id);
      setEditLetter(res.data?.data || res.data || res);
      setFormErrors({});
      setOpenDialog(true);
    } catch {
      toast.error("Failed to fetch handover letter details");
    }
  };

  // DELETE
  const handleDeleteClick = (row) => {
    setDeleteTarget(row);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteHandoverLetter(deleteTarget.id);
      toast.success("Handover letter deleted successfully");
      setConfirmOpen(false);
      setDeleteTarget(null);
      loadLetters();
    } catch (err) {
      toast.error(err.message || "Failed to delete handover letter");
    } finally {
      setDeleting(false);
    }
  };

  // PDF
  const handlePdf = async (row) => {
    setPdfLoading(row.id);
    try {
      await openHandoverLetterPdf(row.id);
    } catch (err) {
      toast.error(err.message || "Failed to open PDF");
    } finally {
      setPdfLoading(null);
    }
  };

  const columns = [
    { field: "generated_id", headerName: "Ref #" },
    { field: "customer.name", headerName: "Customer" },
    { field: "project_location", headerName: "Project Location" },
    {
      field: "total_price",
      headerName: "Total Price",
      render: (r) => `₹${Number(r.total_price || 0).toLocaleString("en-IN")}`,
    },
    {
      field: "letter_date",
      headerName: "Date",
      render: (r) => {
        if (!r.letter_date) return "-";
        return new Date(r.letter_date).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
  ];

  const actions = [
    {
      icon: <PictureAsPdf />,
      label: "PDF",
      onClick: (row) => handlePdf(row),
      backgroundColor: "#ff9800",
    },
    {
      icon: <Edit />,
      label: "Edit",
      onClick: (row) => handleEditClick(row),
      backgroundColor: theme.button.editBackgroundColor,
    },
    {
      icon: <Delete />,
      label: "Delete",
      onClick: (row) => handleDeleteClick(row),
      backgroundColor: theme.button.deleteBackgroundColor,
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box component="h1" sx={{ m: 0 }}>
          Handover Letters
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          sx={{
            borderRadius: theme.button.borderRadius,
            backgroundColor: theme.palette.primary.main,
          }}
          onClick={() => {
            setEditLetter(null);
            setFormErrors({});
            setOpenDialog(true);
          }}
        >
          Create Handover Letter
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <AsyncAutocomplete
          label="Filter by Customer"
          value={filters.customer_id}
          onChange={(id) => setFilters({ ...filters, customer_id: id })}
          fetchPage={fetchCustomerPage}
          getOptionLabel={customerLabel}
          sx={{ minWidth: 220 }}
        />
        <TextField
          size="small"
          label="Search"
          placeholder="Ref #, location or customer name"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          sx={{ minWidth: 260 }}
        />
        <TextField
          type="date"
          size="small"
          label="From Date"
          InputLabelProps={{ shrink: true }}
          value={filters.from_date}
          onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
        />
        <TextField
          type="date"
          size="small"
          label="To Date"
          InputLabelProps={{ shrink: true }}
          value={filters.to_date}
          onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
        />
        {(filters.customer_id || filters.search || filters.from_date || filters.to_date) && (
          <Button
            variant="text"
            onClick={() => {
              setFilters({ customer_id: "", search: "", from_date: "", to_date: "" });
            }}
          >
            Clear Filters
          </Button>
        )}
      </Box>

      {/* Table */}
      <Box>
        <TableComponent
          data={letters || []}
          columns={columns}
          actions={actions}
          loading={loading}
          links={links}
          setCurrentPage={setCurrentPage}
          meta={meta}
          perPage={perPage}
          setPerPage={setPerPage}
        />
      </Box>

      {/* Create / Edit dialog */}
      <CreateHandoverLetterDialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setEditLetter(null);
        }}
        saving={saving}
        onSave={handleSave}
        errors={formErrors}
        letter={editLetter}
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Handover Letter"
        message={`Are you sure you want to delete handover letter ${deleteTarget?.generated_id}? This action cannot be undone.`}
        loading={deleting}
      />
    </Box>
  );
};

export default HandoverLettersManagementPage;
